import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { PatientService } from '../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../utils/helper'

interface QuickAnswerButtonsProps {
  questionnaire: {
    id: string
    questionnaire_id: number
    first_component_type?: string
    first_response?: string | number | null
    needs_confirmation?: boolean
    recent_confirmation?: { confirmed_by?: string; confirmed_on?: number } | null
    options?: string[]
    first_question_id?: string | null // When provided, skips the API call on first click
  }
  patientId: string
  isRecommended: boolean
  onAnswerSaved: (questionnaireId: string, response: any) => void
  onAnswerChanged?: (questionnaireId: string, questionId: string, value: string | number) => void
  disabled?: boolean
  compact?: boolean
  localOnly?: boolean // If true, only call onAnswerChanged without saving to backend
}

/**
 * QuickAnswerButtons - Inline Y/N/U or 1-5 scale buttons for rapid questionnaire entry
 *
 * For simple questionnaires (single question, no branches), allows one-click
 * answer + auto-save+confirm without opening a dialog.
 *
 * Shows:
 * - [Y] [N] [?] for YesNoRadioQuestion/RadioQuestion with 3 options
 * - [Y] [N] for YesNoRadioQuestion/RadioQuestion with only Yes/No
 * - [1] [2] [3] [4] [5] for ScaleQuestion
 * - Nothing for complex questionnaires (user clicks to expand)
 *
 * On first click, fetches the question structure to get the question ID,
 * then caches it for subsequent clicks.
 */
const QuickAnswerButtons: React.FC<QuickAnswerButtonsProps> = ({
  questionnaire,
  patientId,
  isRecommended,
  onAnswerSaved,
  onAnswerChanged,
  disabled = false,
  localOnly = false
}) => {
  const [isSaving, setIsSaving] = useState(false)
  // Optimistic local answer for immediate visual feedback in localOnly mode
  const [localAnswer, setLocalAnswer] = useState<string | number | null>(null)
  const patientService = useMemo(() => new PatientService(), [])

  // Cache for first question ID and branches (fetched on first click)
  const questionIdCache = useRef<string | null>(null)
  const questionBranchesCache = useRef<any[] | null>(null)
  const hasMultipleQuestions = useRef<boolean>(false)

  // Track if current answer has unfilled branch questions
  const [hasPendingBranches, setHasPendingBranches] = useState(false)

  // Determine if this questionnaire supports quick answer based on first_component_type
  // Also check first_response as fallback - if it's a number 1-5, treat as scale
  const questionnaireType = useMemo(() => {
    const componentType = questionnaire.first_component_type?.toLowerCase() || ''

    // Scale questions (1-5) - check component type first
    if (componentType === 'scale' || componentType.includes('scale')) {
      return 'scale'
    }

    // Fallback: check if first_response is a scale value (1-5)
    const response = questionnaire.first_response
    if (response !== null && response !== undefined) {
      const numResponse = typeof response === 'number' ? response : parseFloat(String(response))
      if (!isNaN(numResponse) && numResponse >= 1 && numResponse <= 5 && Number.isInteger(numResponse)) {
        return 'scale'
      }
    }

    // YesNo or Radio questions - treat anything non-scale as yes/no
    return 'yesno'
  }, [questionnaire.first_component_type, questionnaire.first_response])

  // Clear optimistic answer once the parent prop catches up
  useEffect(() => {
    setLocalAnswer(null)
  }, [questionnaire.first_response])

  // Get current answer value for highlighting
  const currentAnswer = useMemo(() => {
    const response = localAnswer !== null ? localAnswer : questionnaire.first_response

    // Normalize common string values to expected casing
    if (typeof response === 'string') {
      const lower = response.toLowerCase()
      if (lower === 'yes') return 'Yes'
      if (lower === 'no') return 'No'
      if (lower === 'unknown') return 'Unknown'
    }

    // If first_response is null/undefined, treat as Unknown
    if (response === null || response === undefined) {
      return 'Unknown'
    }

    return response
  }, [localAnswer, questionnaire.first_response, questionnaire.id, questionnaire.recent_confirmation, questionnaire.needs_confirmation])

  // Helper to check if an answer value triggers a branch
  const checkBranchForValue = useCallback((value: string | number | null, branches: any[] | null): boolean => {
    if (value === 'Unknown' || value === null || !branches || branches.length === 0) {
      return false
    }
    return branches.some(
      (branch: any) => branch.condition === value || branch.condition?.toLowerCase() === String(value).toLowerCase()
    )
  }, [])

  // Fetch question structure to get question ID and branches
  const fetchQuestionInfo = useCallback(async (): Promise<{
    questionId: string | null,
    branches: any[] | null,
    hasMultipleQuestions: boolean
  }> => {
    // Return cached value if available
    if (questionIdCache.current) {
      return {
        questionId: questionIdCache.current,
        branches: questionBranchesCache.current,
        hasMultipleQuestions: hasMultipleQuestions.current
      }
    }

    // first_question_id passed directly — skip the API call entirely so first click is instant
    if (questionnaire.first_question_id) {
      questionIdCache.current = questionnaire.first_question_id
      hasMultipleQuestions.current = false
      return { questionId: questionnaire.first_question_id, branches: null, hasMultipleQuestions: false }
    }

    try {
      const response = await patientService.getQuestionnaireDataset(
        String(questionnaire.questionnaire_id),
        patientId
      )

      const questions = response?.question_set?.questions || []

      if (questions.length === 0) {
        return { questionId: null, branches: null, hasMultipleQuestions: false }
      }

      // Cache the first question ID and its branches
      questionIdCache.current = questions[0].id
      questionBranchesCache.current = questions[0].branches || null
      hasMultipleQuestions.current = questions.length > 1

      return {
        questionId: questions[0].id,
        branches: questions[0].branches || null,
        hasMultipleQuestions: questions.length > 1
      }
    } catch (error) {
      console.error('Failed to fetch questionnaire structure:', error)
      return { questionId: null, branches: null, hasMultipleQuestions: false }
    }
  }, [questionnaire.questionnaire_id, patientId, patientService])

  // Update branch indicator when answer changes. Use warm cache when available;
  // fall back to fetching once when the cache is cold (e.g. on first render with a saved answer).
  useEffect(() => {
    if (currentAnswer === 'Unknown' || currentAnswer === null) {
      setHasPendingBranches(false)
      return
    }
    if (questionBranchesCache.current !== null) {
      setHasPendingBranches(checkBranchForValue(currentAnswer, questionBranchesCache.current))
      return
    }
    let cancelled = false
    fetchQuestionInfo().then(({ branches }) => {
      if (!cancelled) setHasPendingBranches(checkBranchForValue(currentAnswer, branches))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [currentAnswer, fetchQuestionInfo, checkBranchForValue])

  // Handle quick answer click
  const handleQuickAnswer = useCallback(async (value: string | number) => {
    if (isSaving || disabled) return

    // Immediately reflect the clicked value so the button highlights on first click
    if (localOnly) setLocalAnswer(value)

    setIsSaving(true)
    try {
      const { questionId, branches, hasMultipleQuestions } = await fetchQuestionInfo()

      if (!questionId) {
        toast.error('Unable to load questionnaire', Helper.bottom_center())
        setIsSaving(false)
        return
      }

      if (hasMultipleQuestions) {
        toast.info('This questionnaire has multiple questions. Please expand to answer.', Helper.bottom_center())
        setIsSaving(false)
        return
      }

      // Check if this answer triggers follow-up questions
      let answerHasBranches = false
      if (value !== 'Unknown' && branches && branches.length > 0) {
        answerHasBranches = branches.some(
          (branch: any) => branch.condition === value || branch.condition?.toLowerCase() === String(value).toLowerCase()
        )
      }
      setHasPendingBranches(answerHasBranches)

      // Local-only mode: just call the callback without saving
      if (localOnly && onAnswerChanged) {
        onAnswerChanged(questionnaire.id, questionId, value)
        setIsSaving(false)
        return
      }

      // Build and send the save request
      const payload = {
        data: [{
          id: questionnaire.id,
          answer: {
            [questionId]: { value }
          },
          is_recommended: isRecommended,
          confirmed: true
        }]
      }

      const response = await patientService.updateQuestionnaires(patientId, payload)

      if (response?.data?.message === 'Success') {
        onAnswerSaved(questionnaire.id, response.data.result?.[0] || {})
      } else {
        toast.error('Failed to save', Helper.bottom_center())
      }
    } catch (error: any) {
      if (error?.response?.data?.message?.includes('incomplete')) {
        toast.info('Answer saved', Helper.bottom_center())
        onAnswerSaved(questionnaire.id, error.response.data.result?.[0] || {})
      } else {
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      }
    } finally {
      setIsSaving(false)
    }
  }, [questionnaire, patientId, isRecommended, patientService, onAnswerSaved, onAnswerChanged, isSaving, disabled, fetchQuestionInfo, localOnly])

  // Don't render if not a quick-answerable type
  if (!questionnaireType) {
    return null
  }

  // Render loading state — only in non-localOnly mode (localOnly uses optimistic localAnswer instead)
  if (isSaving && !localOnly) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
        <CircularProgress size={14} />
      </Box>
    )
  }

  // NEW: Check the actual options array to determine which buttons to render
  const availableOptions = questionnaire.options || []
  const hasYes = availableOptions.includes('Yes')
  const hasNo = availableOptions.includes('No')
  const hasUnknown = availableOptions.length === 3 // If 3 options, include Unknown button

  // If options exist but don't match Y/N/U pattern AND it's not a scale type,
  // this is a non-standard questionnaire - don't show quick-answer buttons
  // Scale questionnaires have options like ["1","2","3","4","5"] which don't have Yes/No
  if (questionnaireType !== 'scale' && availableOptions.length > 0 && !hasYes && !hasNo) {
    return null
  }
  // Render Yes/No buttons (with or without Unknown based on options)
  if (questionnaireType === 'yesno' && (availableOptions.length === 0 || availableOptions.some(item => ['Yes','No','Unknown'].includes(item)))) {
    // If no options are provided, fall back to default Yes/No/Unknown
    const showUnknown = availableOptions.length === 0 || hasUnknown

    const isNo = currentAnswer === 'No'
    const isUnknown = currentAnswer === 'Unknown'
    const isYes = currentAnswer === 'Yes'

    const baseStyle: React.CSSProperties = {
      minWidth: '16px',
      width: '16px',
      padding: '0',
      fontSize: '0.5rem',
      lineHeight: 1,
      fontWeight: 600,
      height: '16px',
      borderRadius: 0,
      textTransform: 'none' as const
    }

    const noStyle: React.CSSProperties = isNo
      ? {
        ...baseStyle,
        backgroundColor: hasPendingBranches ? 'transparent' : 'var(--color-error)',
        color: hasPendingBranches ? 'var(--color-error)' : 'white',
        border: hasPendingBranches ? '2px dashed var(--color-error)' : '1px solid var(--color-error)'
      }
      : { ...baseStyle, backgroundColor: 'transparent', color: '#ed6c02', border: '1px solid #ed6c02' }

    const unknownStyle: React.CSSProperties = isUnknown
      ? { ...baseStyle, backgroundColor: '#757575', color: 'white', border: '1px solid #757575' }
      : { ...baseStyle, backgroundColor: 'transparent', color: '#9e9e9e', border: '1px solid #bdbdbd' }

    const yesStyle: React.CSSProperties = isYes
      ? {
        ...baseStyle,
        backgroundColor: hasPendingBranches ? 'transparent' : 'var(--color-success)',
        color: hasPendingBranches ? 'var(--color-success)' : 'white',
        border: hasPendingBranches ? '2px dashed var(--color-success)' : '1px solid var(--color-success)'
      }
      : { ...baseStyle, backgroundColor: 'transparent', color: 'var(--color-success)', border: '1px solid var(--color-success)' }

    return (
      <Box
        sx={{ display: 'inline-flex', ml: 0.5, height: 'fit-content' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Always show No button if it's in options or no options provided */}
        {(hasNo || availableOptions.length === 0) && (
          <Button
            onClick={() => handleQuickAnswer('No')}
            disabled={disabled}
            size="small"
            style={noStyle}
          >
            N
          </Button>
        )}

        {/* Show Unknown button only if there are 3 options or no options provided */}
        {showUnknown && (
          <Button
            onClick={() => handleQuickAnswer('Unknown')}
            disabled={disabled}
            size="small"
            style={{
              ...unknownStyle,
              borderLeft: hasNo || availableOptions.length === 0 ? 'none' : undefined,
              borderRight: hasYes || availableOptions.length === 0 ? 'none' : undefined
            }}
          >
            ?
          </Button>
        )}

        {/* Always show Yes button if it's in options or no options provided */}
        {(hasYes || availableOptions.length === 0) && (
          <Button
            onClick={() => handleQuickAnswer('Yes')}
            disabled={disabled}
            size="small"
            style={yesStyle}
          >
            Y
          </Button>
        )}
      </Box>
    )
  }

  // Render 1-5 scale buttons
  if (questionnaireType === 'scale') {
    const baseStyle: React.CSSProperties = {
      minWidth: '16px',
      width: '16px',
      padding: '0',
      fontSize: '0.5rem',
      lineHeight: 1,
      fontWeight: 600,
      height: '16px',
      borderRadius: 0,
      textTransform: 'none' as const
    }

    return (
      <Box
        sx={{ display: 'inline-flex', ml: 0.5, height: 'fit-content' }}
        onClick={(e) => e.stopPropagation()}
      >
        {[1, 2, 3, 4, 5].map((num, index) => {
          const isSelected = currentAnswer === num || currentAnswer === String(num)
          const btnStyle: React.CSSProperties = isSelected
            ? { ...baseStyle, backgroundColor: 'var(--color-answer-scale)', color: 'white', border: '1px solid var(--color-answer-scale)' }
            : { ...baseStyle, backgroundColor: 'transparent', color: '#ed6c02', border: '1px solid #ed6c02' }

          if (index > 0) {
            btnStyle.borderLeft = 'none'
          }

          return (
            <Button
              key={num}
              onClick={() => handleQuickAnswer(num)}
              disabled={disabled}
              size="small"
              style={btnStyle}
            >
              {num}
            </Button>
          )
        })}
      </Box>
    )
  }

  return null
}

export default QuickAnswerButtons